/**
 * @fileoverview Componente responsável por criar e editar filas de atendimento.
 */

import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { ColorBox } from "material-ui-color";
import {
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Tab,
  Tabs,
} from "@material-ui/core";
import { Plus, Pencil, Pipette, X } from "lucide-react";
import { QueueOptions } from "../QueueOptions";
import ModernSelect from "../ModernSelect";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const fieldOutlined = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background: dark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      border: dark
        ? "1px solid rgba(91, 124, 255, 0.15)"
        : "1px solid rgba(0, 0, 0, 0.08)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "& fieldset": { borderColor: "transparent" },
      "&:hover": {
        background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(61, 92, 253, 0.04)",
        border: dark
          ? "1px solid rgba(91, 124, 255, 0.25)"
          : "1px solid rgba(61, 92, 253, 0.15)",
      },
      "&.Mui-focused": {
        background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 1)",
        border: dark
          ? "1px solid rgba(91, 124, 255, 0.4)"
          : "1px solid rgba(61, 92, 253, 0.3)",
        boxShadow: "0 0 0 4px rgba(91, 124, 255, 0.15), 0 8px 24px rgba(91, 124, 255, 0.2)",
      },
    },
  };
  return {
    root: { display: "flex", flexWrap: "wrap" },
    dialog: {
      "& .MuiDialog-paper": {
        fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
        borderRadius: 24,
        overflow: "hidden",
        background: dark
          ? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
          : "#ffffff",
        boxShadow: dark
          ? "0 24px 80px rgba(0, 0, 0, 0.45)"
          : "0 24px 60px rgba(15, 23, 42, 0.12)",
        border: dark
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
      },
      "& .MuiBackdrop-root": {
        backgroundColor: dark ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.4)",
      },
    },
    modalHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(2.5, 3),
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    },
    modalTitle: {
      fontSize: "1.35rem",
      fontWeight: 700,
      color: dark ? "#f8fafc" : "#111827",
    },
    modalClose: {
      color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
    },
    tabsBar: {
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.06)",
      background: "transparent",
      "& .MuiTab-root": {
        textTransform: "none",
        fontWeight: 500,
      },
    },
    contentPaper: {
      background: "transparent",
      boxShadow: "none",
    },
    textField: {
      marginRight: theme.spacing(1),
      flex: 1,
      ...fieldOutlined,
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
      ...fieldOutlined,
    },
    colorAdorment: {
      width: 22,
      height: 22,
      borderRadius: 6,
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.18)"
        : "1px solid rgba(15, 23, 42, 0.18)",
    },
    colorPopover: {
      "& .MuiPopover-paper": {
        marginTop: 8,
        borderRadius: 16,
        padding: theme.spacing(1.5),
        background: dark
          ? "linear-gradient(180deg, #1a1d24 0%, #14161c 100%)"
          : "#ffffff",
        border: dark
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: dark
          ? "0 16px 40px rgba(0, 0, 0, 0.5)"
          : "0 16px 40px rgba(15, 23, 42, 0.15)",
        overflow: "visible",
      },
    },

    // ========= Footer / Actions =========
    dialogActions: {
      padding: theme.spacing(2, 3),
      borderTop: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    cancelLeft: { marginRight: "auto" },

    btnBase: {
      height: 38,
      padding: "0 16px",
      borderRadius: 10,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      whiteSpace: "nowrap",
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      border: "none",
      boxShadow: "none",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      "& svg": { flexShrink: 0 },
      "& .MuiButton-label": { whiteSpace: "nowrap" },
    },

    btnCancel: {
      background: "transparent",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
      color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
      "&:hover": {
        background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
        color: dark ? "#f8fafc" : "#111827",
      },
    },

    btnPrimary: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "&:hover": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
      "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
    },

    spinner: { color: "#ffffff" },
  };
});

const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("queueModal.form.nameShort"))
    .max(50, i18n.t("queueModal.form.nameLong"))
    .required(i18n.t("queueModal.form.nameRequired")),
  color: Yup.string()
    .min(3, i18n.t("queueModal.form.colorShort"))
    .max(9, i18n.t("queueModal.form.colorLong"))
    .required(),
  greetingMessage: Yup.string(),
});

const INITIAL_QUEUE_STATE = {
  name: "",
  color: "",
  greetingMessage: "",
  outOfHoursMessage: "",
  orderQueue: "",
  integrationId: "",
};

const QueueModal = ({ open, onClose, queueId }) => {
  const classes = useStyles();

  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [queue, setQueue] = useState(INITIAL_QUEUE_STATE);
  const [tab, setTab] = useState(0);
  const greetingRef = useRef();
  const colorAnchorRef = useRef(null);
  const [integrations, setIntegrations] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queueIntegration");

        setIntegrations(data.queueIntegrations);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!open) return;

      if (!queueId) {
        setQueue({ ...INITIAL_QUEUE_STATE });
        return;
      }

      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setQueue((prevState) => {
          return { ...prevState, ...data };
        });
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setQueue({ ...INITIAL_QUEUE_STATE });
    };
  }, [queueId, open]);

  const handleClose = () => {
    onClose();
    setQueue({ ...INITIAL_QUEUE_STATE });
    setTab(0);
  };

  const handleSaveQueue = async (values) => {
    try {
      if (queueId) {
        await api.put(`/queue/${queueId}`, values);
      } else {
        await api.post("/queue", values);
      }
      toast.success(i18n.t("queueModal.toasts.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        maxWidth="md"
        fullWidth={true}
        open={open}
        onClose={handleClose}
        scroll="paper"
        className={classes.dialog}
      >
        <div className={classes.modalHeader}>
          <Typography className={classes.modalTitle}>
            {queueId
              ? i18n.t("queueModal.title.edit")
              : i18n.t("queueModal.title.add")}
          </Typography>
          <IconButton size="small" onClick={handleClose} className={classes.modalClose}>
            <X size={18} strokeWidth={1.75} />
          </IconButton>
        </div>
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_, v) => setTab(v)}
          aria-label="queue modal tabs"
          className={classes.tabsBar}
        >
          <Tab label={i18n.t("queueModal.tabs.queueData")} />
        </Tabs>
        {tab === 0 && (
          <Paper className={classes.contentPaper}>
            <Formik
              initialValues={queue}
              enableReinitialize={true}
              validationSchema={QueueSchema}
              validateOnChange={true}
              validateOnBlur={false}
              validateOnMount={true}
              onSubmit={(values, actions) => {
                setTimeout(() => {
                  handleSaveQueue(values);
                  actions.setSubmitting(false);
                }, 400);
              }}
            >
              {({ errors, isSubmitting, isValid, values, submitCount, setFieldValue }) => (
                <Form>
                  <DialogContent dividers>
                    <Field
                      as={TextField}
                      label={i18n.t("queueModal.form.name")}
                      autoFocus
                      name="name"
                      error={submitCount > 0 && Boolean(errors.name)}
                      helperText={submitCount > 0 ? errors.name : ""}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                    <Field
                      as={TextField}
                      label={i18n.t("queueModal.form.color")}
                      name="color"
                      id="color"
                      error={submitCount > 0 && Boolean(errors.color)}
                      helperText={submitCount > 0 ? errors.color : ""}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <div
                              style={{ backgroundColor: values.color }}
                              className={classes.colorAdorment}
                            ></div>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <IconButton
                            size="small"
                            ref={colorAnchorRef}
                            onClick={() => setColorPickerOpen((open) => !open)}
                          >
                            <Pipette size={18} strokeWidth={1.75} />
                          </IconButton>
                        ),
                      }}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                    <Popover
                      open={colorPickerOpen}
                      anchorEl={colorAnchorRef.current}
                      onClose={() => setColorPickerOpen(false)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      className={classes.colorPopover}
                    >
                      <ColorBox
                        disableAlpha={true}
                        hslGradient={false}
                        value={values.color}
                        onChange={(val) => {
                          setFieldValue("color", `#${val.hex}`);
                        }}
                      />
                    </Popover>
                    <Field
                      as={TextField}
                      label={i18n.t("queueModal.form.orderQueue")}
                      name="orderQueue"
                      type="orderQueue"
                      error={submitCount > 0 && Boolean(errors.orderQueue)}
                      helperText={submitCount > 0 ? errors.orderQueue : ""}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                    <div>
                      <ModernSelect
                        label={i18n.t("queueModal.form.integrationId")}
                        name="integrationId"
                        id="integrationId"
                        value={values.integrationId || ""}
                        onChange={(e) =>
                          setFieldValue("integrationId", e.target.value)
                        }
                        formControlProps={{
                          margin: "dense",
                          fullWidth: true,
                          className: classes.formControl,
                        }}
                      >
                        <MenuItem value="">Nenhum</MenuItem>
                        {integrations.map((integration) => (
                          <MenuItem key={integration.id} value={integration.id}>
                            {integration.name}
                          </MenuItem>
                        ))}
                      </ModernSelect>
                    </div>
                    <div style={{ marginTop: 5 }}>
                      <Field
                        as={TextField}
                        label={i18n.t("queueModal.form.greetingMessage")}
                        type="greetingMessage"
                        multiline
                        inputRef={greetingRef}
                        rows={5}
                        fullWidth
                        name="greetingMessage"
                        error={
                          submitCount > 0 && Boolean(errors.greetingMessage)
                        }
                        helperText={
                          submitCount > 0 ? errors.greetingMessage : ""
                        }
                        variant="outlined"
                        margin="dense"
                        className={classes.textField}
                      />
                    </div>
                    <QueueOptions queueId={queueId} />
                  </DialogContent>
                  <DialogActions className={classes.dialogActions}>
                    <Button
                      onClick={handleClose}
                      disabled={isSubmitting}
                      className={`${classes.btnBase} ${classes.btnCancel} ${classes.cancelLeft}`}
                      startIcon={<X size={16} strokeWidth={1.75} />}
                    >
                      {i18n.t("queueModal.buttons.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting || !isValid}
                      className={`${classes.btnBase} ${classes.btnPrimary}`}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={14} className={classes.spinner} />
                        ) : queueId ? (
                          <Pencil size={16} strokeWidth={1.75} />
                        ) : (
                          <Plus size={16} strokeWidth={1.75} />
                        )
                      }
                    >
                      {queueId
                        ? i18n.t("queueModal.buttons.okEdit")
                        : i18n.t("queueModal.buttons.okAdd")}
                    </Button>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Paper>
        )}
      </Dialog>
    </div>
  );
};

export default QueueModal;
